import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AI_USAGE_KINDS, type AdminRange, type AiUsageKind } from '@b4b/shared';
import { adminTimeseriesQueryOptions } from '../../api/queries.js';
import StackedBars, { type Series } from './StackedBars.js';

const COLORS: Record<AiUsageKind, string> = {
  embedding: '#6366f1',
  image: '#ec4899',
  tags: '#10b981',
  translation: '#f59e0b',
  junk_rules: '#64748b',
};

export default function AiTimeseriesChart({ range }: { range: AdminRange }) {
  const { t } = useTranslation();
  // One small request per kind (5, cached 60 s) rather than calling useAdminTimeseries
  // inside a .map() — useQueries keeps the hook count/order stable regardless of data.
  const results = useQueries({
    queries: AI_USAGE_KINDS.map((kind) => adminTimeseriesQueryOptions(range, kind)),
  });
  if (results.some((r) => r.isError)) return <p className="text-sm text-red-600 dark:text-red-400">{t('admin.loadError')}</p>;
  const ready = results.every((r) => r.data);
  if (!ready) return <p className="text-sm text-neutral-500">{t('common.loading')}</p>;
  const labels = results[0]!.data!.days.map((d) => d.date);
  const series: Series[] = AI_USAGE_KINDS.map((kind, i) => ({
    key: kind,
    label: t(`admin.ai.kind.${kind}`),
    color: COLORS[kind],
    values: results[i]!.data!.days.map((d) => d.calls),
  }));
  const failed = labels.map((_, i) => results.reduce((s, r) => s + (r.data!.days[i]?.failed ?? 0), 0));
  return (
    <section className="rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
      <h2 className="mb-2 font-semibold">{t('admin.ai.chartTitle')}</h2>
      <StackedBars labels={labels} series={series} overlay={{ label: t('admin.ai.chartFailed'), color: '#ef4444', values: failed }} ariaLabel={t('admin.ai.chartTitle')} />
    </section>
  );
}
